import { useCallback, useEffect, useMemo, useRef } from "react";
import type {
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import type { Geofence } from "../../../../../../store/admin/geofencesStore";
import type { ThemeClasses, PointCoordinate } from "../types";
import MapPicker from "../../../../../../components/base/MapPicker";

// Average lat/lng of a list of points. Used to pick the "address" of a
// polygonal geofence: the centroid is more representative of where the
// geofence sits than any single vertex.
function centroidOf(points: PointCoordinate[]): PointCoordinate | null {
  if (!points || points.length === 0) return null;
  let lat = 0;
  let lng = 0;
  for (const p of points) {
    lat += Number(p.latitude);
    lng += Number(p.longitude);
  }
  return { latitude: lat / points.length, longitude: lng / points.length };
}

/**
 * Reverse-geocodes (lat, lng) → formatted_address using the already-loaded
 * Google Maps SDK. Debounced so a stream of drag updates only triggers one
 * request once the user settles. Skips the very first valid observation so
 * the initial mount (edit mode) or the default coords set on shape change
 * (create mode) don't overwrite the address with a geocode the user never
 * asked for — the FIRST real user-driven coordinate change does fire.
 *
 * IMPORTANT: `onResolved` is captured in a ref so re-renders that change its
 * identity don't restart the debounce timer. Without this, the parent's
 * inline arrow (`(addr) => setValue(...)`) made the effect re-run on every
 * render, cancelling the pending timer before it could fire — and the user
 * saw "moving the marker doesn't update the address".
 */
function useReverseGeocodedAddress(
  point: PointCoordinate | null,
  onResolved: (address: string) => void,
  options: { skipFirstRun?: boolean; debounceMs?: number } = {}
) {
  const { skipFirstRun = false, debounceMs = 300 } = options;
  const skipRef = useRef(skipFirstRun);
  const callbackRef = useRef(onResolved);

  // Keep the latest callback in a ref so the effect below doesn't depend on
  // it. The deps array only tracks the coordinates and the debounce window.
  useEffect(() => {
    callbackRef.current = onResolved;
  }, [onResolved]);

  useEffect(() => {
    if (!point) return;
    if (typeof google === "undefined" || !google?.maps?.Geocoder) return;
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    const handle = setTimeout(() => {
      const geocoder = new google.maps.Geocoder();
      geocoder
        .geocode({ location: { lat: point.latitude, lng: point.longitude } })
        .then((result) => {
          const formatted = result.results?.[0]?.formatted_address;
          if (formatted) callbackRef.current(formatted);
        })
        .catch(() => {
          // Reverse-geocoding is a nice-to-have; failing silently is fine.
        });
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [point?.latitude, point?.longitude, debounceMs]);
}

interface GeofenceMapPreviewProps {
  shape: string;
  form: {
    watch: UseFormWatch<Geofence>;
    setValue: UseFormSetValue<Geofence>;
    trigger: UseFormTrigger<Geofence>;
  };
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isEdit: boolean;
  polygonPoints: PointCoordinate[];
  setPolygonPoints: (points: PointCoordinate[]) => void;
  linearPoints: PointCoordinate[];
  defaultCircularCoordinates: {
    latitude: number;
    longitude: number;
    radius: number;
  };
  onLinearPointsChange: (newPoints: { lat: number; lng: number }[]) => void;
  onAxisCalculated: (path: PointCoordinate[]) => void;
  corridorBuffer: number;
  onRouteCalculated: (distance: number) => void;
  routeDistance: number;
  estimatedTime: string;
  polygonArea: number;
}

export function GeofenceMapPreview({
  shape,
  form,
  themeClasses,
  isEdit,
  polygonPoints,
  setPolygonPoints,
  linearPoints,
  defaultCircularCoordinates,
  onLinearPointsChange,
  onAxisCalculated,
  corridorBuffer,
  onRouteCalculated,
  routeDistance,
  estimatedTime,
  polygonArea,
}: GeofenceMapPreviewProps) {
  const { watch, setValue, trigger } = form;

  const containerClassName = "w-full h-full flex flex-col bg-app";

  // Watch form values once and use them consistently
  const circularLat = watch("coordinates.latitude");
  const circularLng = watch("coordinates.longitude");
  const circularRadius = watch("coordinates.radius");

  // Reverse-geocoding source point per shape.
  //   • circular: the marker (= circle center).
  //   • linear:   the first point (origin of the route).
  //   • polygonal: the centroid of the vertices.
  let geocodePoint: PointCoordinate | null = null;
  const numLat = Number(circularLat);
  const numLng = Number(circularLng);
  if (shape === "circular" && Number.isFinite(numLat) && Number.isFinite(numLng)) {
    geocodePoint = { latitude: numLat, longitude: numLng };
  } else if (shape === "linear" && linearPoints.length > 0) {
    geocodePoint = linearPoints[0];
  } else if (shape === "polygonal") {
    geocodePoint = centroidOf(polygonPoints);
  }

  // Temporarily disabled reverse-geocoding to diagnose marker movement bug
  // Always skip the first run: in edit mode we already have a stored address,
  // and in create mode the initial coordinates are arbitrary defaults (CDMX
  // center), so resolving them on mount would write a meaningless address the
  // user never asked for. After mount, every subsequent coordinate change —
  // whether from a marker drag, a polygon vertex move, or even an autocomplete
  // select — triggers a debounced reverse-geocode so the address stays in sync
  // with the actual point on the map.
  // useReverseGeocodedAddress(
  //   geocodePoint,
  //   (address) => {
  //     setValue("address", address, { shouldValidate: false, shouldDirty: false });
  //   },
  //   { skipFirstRun: true }
  // );

  const memoizedCircularCoords = useMemo(() => ({
    lat: Number.isFinite(Number(circularLat)) ? Number(circularLat) : defaultCircularCoordinates.latitude,
    lng: Number.isFinite(Number(circularLng)) ? Number(circularLng) : defaultCircularCoordinates.longitude,
    radius: Number.isFinite(Number(circularRadius)) ? Number(circularRadius) : defaultCircularCoordinates.radius,
  }), [circularLat, circularLng, circularRadius]);

  // Memoize callbacks to prevent unnecessary Marker re-renders and drag issues
  const handleLocationSelect = useCallback((newLat: number, newLng: number) => {
    setValue("coordinates.latitude", newLat);
    setValue("coordinates.longitude", newLng);
    trigger("coordinates.latitude");
    trigger("coordinates.longitude");
  }, [setValue, trigger]);

  const handleRadiusChange = useCallback((newRadius: number) => {
    setValue("coordinates.radius", newRadius);
    trigger("coordinates.radius");
  }, [setValue, trigger]);

  if (shape === "circular") {
    return (
      <div className={containerClassName}>
        <MapPicker
          shape="circular"
          minHeight={320}
          type={watch("type")}
          lat={memoizedCircularCoords.lat}
          lng={memoizedCircularCoords.lng}
          radius={memoizedCircularCoords.radius}
          onLocationSelect={handleLocationSelect}
          onRadiusChange={handleRadiusChange}
        />
      </div>
    );
  }

  if (shape === "linear") {
    return (
      <div className={containerClassName}>
        <label className={`w-full text-sm font-medium ${themeClasses.text}`}>
          Vista previa de la ruta
        </label>
        <MapPicker
          shape="linear"
          type={watch("type")}
          coordinates={linearPoints.map((p) => ({
            lat: Number(p.latitude),
            lng: Number(p.longitude),
          }))}
          onPointsChange={onLinearPointsChange}
          onAxisCalculated={(path) =>
            onAxisCalculated(
              path.map((p) => ({ latitude: p.lat, longitude: p.lng }))
            )
          }
          corridorBuffer={corridorBuffer}
          onRouteCalculated={onRouteCalculated}
          estimatedTime={estimatedTime}
          polygonArea={polygonArea}
          routeDistance={routeDistance}
          lat={linearPoints[0]?.latitude || 19.4326}
          lng={linearPoints[0]?.longitude || -99.1332}
          radius={0}
          isEdit={isEdit}
        />
        <ul className="w-full flex flex-col justify-start text-xs text-content-faint mt-1 list-disc list-inside space-y-1">
          <li>
            La geocerca lineal requiere mínimo 2 puntos para trazar una ruta.
          </li>
          <li>
            Google Maps calculará la ruta óptima entre los puntos marcados.
          </li>
          <li>Agregue o elimine puntos para definir rutas más complejas.</li>
          <li>Puede arrastrar los puntos existentes para modificar la ruta.</li>
        </ul>
      </div>
    );
  }

  if (shape === "polygonal") {
    const mapPoints = polygonPoints.map((p) => ({
      lat: Number(p.latitude),
      lng: Number(p.longitude),
    }));

    return (
      <div className={containerClassName}>
        <MapPicker
          shape="polygonal"
          minHeight={320}
          type={watch("type")}
          coordinates={mapPoints}
          onPointsChange={(newPoints) => {
            const newPolygonPoints = newPoints.map((p) => ({
              latitude: p.lat,
              longitude: p.lng,
            }));
            setPolygonPoints(newPolygonPoints);
            setValue("coordinates", newPolygonPoints);
          }}
          estimatedTime={estimatedTime}
          polygonArea={polygonArea}
          routeDistance={routeDistance}
          lat={mapPoints[0]?.lat || 0}
          lng={mapPoints[0]?.lng || 0}
          radius={0}
        />
      </div>
    );
  }

  return null;
}
